import { useState } from 'react'
import { MessageSquare } from 'lucide-react'

import { Textarea } from '@/components/ui/textarea'
import { useEditorDraft } from '@/hooks/useEditorDraft'
import { useRegisterTabEditor } from '@/hooks/useRegisterTabEditor'
import {
  DutyCard,
  DutyCardBody,
  DutyCardContent,
  DutyCardEditFooter,
  DutyCardHeader,
} from '@/features/duties/components/cards/shared/DutyCard'
import type { EvalCaseDetail } from '../../types'
import { useUpdateEvalCaseMutation } from '../../hooks/useUpdateEvalCaseMutation'

export function EvalPromptCard({ evalCase }: { evalCase: EvalCaseDetail }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const updateCase = useUpdateEvalCaseMutation(evalCase.id)
  const { draft, editorKey, dirty, onChange, reset, save, isSaving } =
    useEditorDraft(evalCase.system_prompt ?? '', {
      onSave: async (next) => {
        await updateCase.mutateAsync({ system_prompt: next })
      },
      successMessage: 'Prompt saved',
    })

  // useEditorDraft.save already returns a SaveResult, so no adapter is needed.
  useRegisterTabEditor({
    label: 'Counterparty prompt',
    isDirty: () => dirty,
    save,
  })

  return (
    <DutyCard>
      <DutyCardHeader
        icon={<MessageSquare />}
        title="Counterparty prompt"
        expanded={isExpanded}
        onToggleExpanded={() => setIsExpanded((prev) => !prev)}
        expandLabel="Edit counterparty prompt"
        collapseLabel="Collapse counterparty prompt"
      />
      <DutyCardBody>
        <DutyCardContent>
          {isExpanded ? (
            <Textarea
              key={editorKey}
              value={draft}
              onChange={(e) => onChange(e.target.value)}
              rows={10}
              aria-label="Counterparty prompt"
            />
          ) : evalCase.system_prompt ? (
            <div className="flex flex-col gap-2">
              <div className="relative max-h-40 min-h-10 overflow-hidden">
                <p className="m-0 whitespace-pre-wrap text-[13px] leading-[1.55] text-neutral-low-contrast">
                  {evalCase.system_prompt}
                </p>
                {/* Bottom fade so the clipped brief bleeds out, matching duty script cards. */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-card to-transparent" />
              </div>
              <p className="m-0 text-[11.5px] text-neutral-low-contrast">
                {evalCase.system_prompt.length} characters
              </p>
            </div>
          ) : (
            <p className="m-0 text-[13px] text-neutral-low-contrast">
              No counterparty prompt yet.
            </p>
          )}
        </DutyCardContent>
        {isExpanded ? (
          <DutyCardEditFooter
            onCancel={reset}
            onSave={save}
            onCollapse={() => setIsExpanded(false)}
            isDirty={dirty}
            isSaving={isSaving}
          />
        ) : null}
      </DutyCardBody>
    </DutyCard>
  )
}
